// src/hooks/useVerification.js
import { useState, useEffect, useCallback, useRef } from 'react';
import VerificationAPI from '../services/api/verificationAPI';

// Constants for localStorage keys
const VERIFICATION_TOKEN_KEY = 'verification_token';
const VERIFIED_PHONE_KEY = 'verified_phone_number';

/**
 * React hook for managing phone verification state and flow
 * Integrates with Firebase Functions backend for secure Twilio verification
 */
export function useVerification(businessSlug = null) {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [step, setStep] = useState('phone'); // 'phone', 'code', 'verified', 'error'
    const [processLoading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [deviceId, setDeviceId] = useState(null);
    const [verificationToken, setVerificationToken] = useState(null);
    const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState(null);
    const [attemptsRemaining, setAttemptsRemaining] = useState(null);

    // Track if component is mounted to prevent state updates after unmount
    const isMountedRef = useRef(true);

    // Initialize device ID and check for existing verification on mount
    useEffect(() => {
        const initializeVerification = async () => {
            try {
                // Generate or retrieve device ID
                const currentDeviceId = VerificationAPI.generateDeviceId();
                if (isMountedRef.current) {
                    setDeviceId(currentDeviceId);
                }

                // Check for existing verification token
                const storedToken = localStorage.getItem(VERIFICATION_TOKEN_KEY);
                const storedPhone = localStorage.getItem(VERIFIED_PHONE_KEY);

                if (storedToken && storedPhone && currentDeviceId) {
                    // Validate existing token
                    try {
                        const validation = await VerificationAPI.validateVerification(storedToken, currentDeviceId);

                        if (validation.valid) {
                            setVerificationToken(storedToken);
                            setVerifiedPhoneNumber(storedPhone);
                            setPhoneNumber(storedPhone);
                            setStep('verified');
                            console.log('Hook: Restored existing verification', { phoneNumber: storedPhone });
                        } else {
                            // Invalid token, clear storage
                            clearStoredVerification();
                        }
                    } catch (validationError) {
                        console.warn('Hook: Token validation failed, clearing stored verification', validationError.message);
                        clearStoredVerification();
                    }
                }
            } catch (error) {
                console.error('Hook: Failed to initialize verification', error);
                if (isMountedRef.current) {
                    setError('Failed to initialize verification system');
                }
            }
        };

        initializeVerification();

        // Cleanup function
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    /**
     * Clear stored verification data from localStorage
     */
    const clearStoredVerification = useCallback(() => {
        localStorage.removeItem(VERIFICATION_TOKEN_KEY);
        localStorage.removeItem(VERIFIED_PHONE_KEY);
        if (isMountedRef.current) {
            setVerificationToken(null);
            setVerifiedPhoneNumber(null);
        }
    }, []);

    /**
     * Store verification data in localStorage
     */
    const storeVerificationData = useCallback((token, phone) => {
        try {
            localStorage.setItem(VERIFICATION_TOKEN_KEY, token);
            localStorage.setItem(VERIFIED_PHONE_KEY, phone);
        } catch (error) {
            console.error('Hook: Failed to store verification data', error);
        }
    }, []);

    /**
     * Normalize phone number format for consistency
     */
    const normalizePhoneNumber = useCallback((phone) => {
        if (!phone) return '';

        // Remove all spaces and special characters except +
        let normalized = phone.replace(/[^\d+]/g, '');

        // If starts with 0, replace with +972
        if (normalized.startsWith('0')) {
            normalized = '+972' + normalized.substring(1);
        }

        // If doesn't start with +, assume Israeli number
        if (!normalized.startsWith('+')) {
            normalized = '+972' + normalized;
        }

        return normalized;
    }, []);

    /**
     * Initiate verification by sending SMS code
     */
    const initiateVerification = useCallback(async (phoneNumber) => {
        if (!deviceId) {
            setError('Device not initialized. Please refresh the page.');
            return false;
        }

        const normalizedPhone = normalizePhoneNumber(phoneNumber);

        if (!normalizedPhone) {
            setError('Please enter a valid phone number');
            setStep('error');
            return false;
        }

        setPhoneNumber(phoneNumber)
        setLoading(true);
        setError('');

        try {
            if (!businessSlug) {
                setError('Business context is required for verification');
                setStep('error');
                return false;
            }

            console.log('Hook: Initiating verification', { phoneNumber: normalizedPhone, deviceId });
            const result = await VerificationAPI.initiateVerification(normalizedPhone, businessSlug, deviceId);

            if (!result || !('attemptsRemaining' in result)) {
                throw new Error('Unexpected response from verification service');
            }

            setPhoneNumber(normalizedPhone); // Normalized
            setStep('code'); // 🟢 Move to next step
            setAttemptsRemaining(result.attemptsRemaining);
            setError('');

            return result;

        } catch (error) {
            console.error('Hook: Failed to initiate verification', error);
            if (isMountedRef.current) {
                setError(error.message || 'Failed to send verification code');
                setStep('error');
            }
            return false;
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [phoneNumber, deviceId, normalizePhoneNumber]);


    /**
     * Verify the SMS code entered by user
     */
    const verifyCode = useCallback(async () => {
        if (!deviceId) {
            setError('Device not initialized. Please refresh the page.');
            return false;
        }

        if (!verificationCode || !/^\d{5}$/.test(verificationCode)) {
            setError('Please enter a valid 5-digit verification code');
            return false;
        }

        setLoading(true);
        setError('');

        try {
            console.log('Hook: Verifying code', { phoneNumber, deviceId, codeLength: verificationCode.length });

            if (!businessSlug) {
                setError('Business context is required for verification');
                return false;
            }

            const result = await VerificationAPI.verifyCode(phoneNumber, verificationCode, businessSlug, deviceId);

            if (result.success && result.verificationToken) {
                // Store verification data
                storeVerificationData(result.verificationToken, phoneNumber);

                setVerificationToken(result.verificationToken);
                setVerifiedPhoneNumber(phoneNumber);
                setStep('verified');

                console.log('Hook: Verification completed successfully', {
                    phoneNumber,
                    hasToken: !!result.verificationToken
                });

                return {success: true, phoneNumber: phoneNumber};
            } else {
                throw new Error('Verification failed - invalid code');
            }

        } catch (error) {
            console.error('Hook: Failed to verify code', error);
            if (isMountedRef.current) {
                setError(error.message);
                // Don't change step - let user try again
            }
            return false;
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [phoneNumber, verificationCode, deviceId, storeVerificationData]);

    /**
     * Validate current verification token
     */
    const validateCurrentVerification = useCallback(async () => {
        if (!verificationToken || !deviceId) {
            return { valid: false, reason: 'No verification token or device ID' };
        }

        try {
            const result = await VerificationAPI.validateVerification(verificationToken, deviceId);

            if (!result.valid && isMountedRef.current) {
                // Token is invalid, clear stored data
                clearStoredVerification();
                setStep('phone');
                setVerificationToken(null);
                setVerifiedPhoneNumber(null);
            }

            return result;
        } catch (error) {
            console.error('Hook: Failed to validate verification', error);
            return { valid: false, reason: error.message };
        }
    }, [verificationToken, deviceId, clearStoredVerification]);

    /**
     * Reset the verification flow to start over
     */
    const resetVerification = useCallback(() => {
        if (isMountedRef.current) {
            setPhoneNumber('');
            setVerificationCode('');
            setStep('phone');
            setError('');
            setAttemptsRemaining(null);
            // Keep deviceId, verificationToken, and verifiedPhoneNumber for session persistence
        }
    }, []);

    /**
     * Completely clear verification and start fresh
     */
    const clearVerification = useCallback(() => {
        clearStoredVerification();
        VerificationAPI.clearDeviceId();

        if (isMountedRef.current) {
            setPhoneNumber('');
            setVerificationCode('');
            setStep('phone');
            setError('');
            setDeviceId(null);
            setVerificationToken(null);
            setVerifiedPhoneNumber(null);
            setAttemptsRemaining(null);
        }

        // Reinitialize device ID
        setTimeout(() => {
            if (isMountedRef.current) {
                const newDeviceId = VerificationAPI.generateDeviceId();
                setDeviceId(newDeviceId);
            }
        }, 100);
    }, [clearStoredVerification]);

    /**
     * Check if user is currently verified
     */
    const isVerified = step === 'verified' && !!verificationToken && !!verifiedPhoneNumber;

    /**
     * Check if verification is in progress
     */
    const isInProgress = step === 'code';

    /**
     * Check if verification has an error
     */
    const hasError = step === 'error' || !!error;

    return {
        // State
        phoneNumber,
        verificationCode,
        step,
        processLoading,
        error,
        deviceId,
        verificationToken,
        verifiedPhoneNumber,
        attemptsRemaining,

        // Computed state
        isVerified,
        isInProgress,
        hasError,

        // Input handlers
        setPhoneNumber,
        setVerificationCode,

        // Actions
        initiateVerification,
        verifyCode,
        validateCurrentVerification,
        resetVerification,
        clearVerification,

        // Utilities
        normalizePhoneNumber,
        clearError: () => setError(''),
    };
}